'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

// Shared numbered pager — design search.jsx:592-608. Renders ‹ prev, a
// windowed set of page buttons ([1 … cur-1 cur cur+1 … last]) with the active
// one filled, and next ›. Used by the grid pager, the map's floating overlay,
// and the district-list footer so all three paginate identically.

/** Windowed page list: always 1 and last, plus current ±1, gaps become '…'. */
export function paginationRange(current: number, total: number): Array<number | '...'> {
  if (total <= 1) return [1]
  const pages = new Set<number>([1, total, current, current - 1, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)
  const items: Array<number | '...'> = []
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push('...')
    items.push(sorted[i])
  }
  return items
}

export interface PageNavProps {
  page: number
  totalPages: number
  onChange: (page: number) => void
  /** Disables every control (e.g. while a page is loading). */
  disabled?: boolean
  /** Swaps the number row for a spinner — the map fetches a whole batch per page. */
  loading?: boolean
  /** 'md' = grid row; 'sm' = compact pill for the map overlay / list footer. */
  size?: 'sm' | 'md'
  className?: string
}

export function PageNav({ page, totalPages, onChange, disabled, loading, size = 'md', className }: PageNavProps) {
  const { t } = useI18n()
  if (totalPages <= 1) return null

  const sm = size === 'sm'
  const items = paginationRange(page, totalPages)

  // Touch first: 44px targets on phones, back to the tighter desktop sizes
  // from sm where a mouse is doing the pointing.
  const arrowCls = cn(
    'inline-flex items-center justify-center rounded-full transition-transform active:scale-[0.92] disabled:cursor-not-allowed',
    sm
      ? 'h-9 w-9 hover:bg-surface-high disabled:opacity-40 sm:h-7 sm:w-7'
      : 'h-11 w-11 bg-surface-high disabled:opacity-45 sm:h-9 sm:w-9'
  )
  const chevronCls = sm ? 'h-4 w-4' : 'h-[19px] w-[19px]'
  const numCls = (active: boolean) =>
    cn(
      'rounded-full font-mono font-bold transition-colors disabled:cursor-not-allowed',
      sm
        ? 'h-9 min-w-9 px-1.5 text-[12px] sm:h-7 sm:min-w-[28px]'
        : 'h-11 w-11 text-[14px] sm:h-9 sm:w-9 sm:text-[13px]',
      active ? 'bg-primary text-white' : 'text-foreground/80 hover:bg-surface-high'
    )

  return (
    // The compact pager must NOT wrap. It sits in a shrink-to-fit floating
    // overlay on the map, so wrapping is self-reinforcing: it wraps, that
    // keeps the overlay narrow, which keeps it wrapping. (Measured on a 375px
    // phone: overlay 187px, single row needs 204px, so the next-arrow dropped
    // to a second line.) flex-nowrap makes the row's min-content width push
    // the overlay wide enough instead — worst case ~260px, still well inside
    // a 320px screen. The grid pager keeps wrapping: it spans a full-width
    // container where a long page list genuinely needs a second line.
    <div className={cn('flex items-center justify-center', sm ? 'flex-nowrap gap-0.5' : 'flex-wrap gap-1.5', className)}>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={disabled || page <= 1}
        aria-label={t('search.previousPage', 'Previous page')}
        title={t('search.previousPage', 'Previous page')}
        className={arrowCls}
      >
        <ChevronLeft className={chevronCls} />
      </button>

      <div className={cn('flex items-center', sm ? 'gap-0.5' : 'gap-1.5')}>
        {loading ? (
          <div
            className={cn(
              'animate-spin rounded-full border-2 border-primary border-t-transparent',
              sm ? 'mx-1 h-4 w-4' : 'mx-2 h-5 w-5'
            )}
          />
        ) : (
          items.map((item, i) =>
            item === '...' ? (
              <span
                key={`ellipsis-${i}`}
                className={cn('text-ink-muted', sm ? 'px-0.5 text-[12px]' : 'px-1.5 text-sm')}
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onChange(item)}
                disabled={disabled}
                aria-label={`${t('search.page', 'Page')} ${item}`}
                aria-current={item === page ? 'page' : undefined}
                className={numCls(item === page)}
              >
                {item}
              </button>
            )
          )
        )}
      </div>

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={disabled || page >= totalPages}
        aria-label={t('search.nextPage', 'Next page')}
        title={t('search.nextPage', 'Next page')}
        className={arrowCls}
      >
        <ChevronRight className={chevronCls} />
      </button>
    </div>
  )
}
